import React, { useCallback, useMemo, useState } from "react";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import styles from "./SelectContactModal.module.scss";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { SearchInput } from "@whatslly/ui-components/v2/BaseElements/SearchInput";
import { observer } from "mobx-react-lite";
import { useDependencyInjector } from "@whatslly/commons/di";
import { TuvisContactsStore } from "@whatslly/ui-components/Pages/TuvisMessaging/services/TuvisContactsStore";
import { ContactImageComponent } from "@whatslly/ui-components/ContactImageComponent";
import { Button } from "@whatslly/ui-components/v2/Button";

interface SelectContactModalProps extends IModalInstance<{ contactId: string }> {
  hideCreateButton?: boolean;
}

export const SelectContactModal: React.FC<SelectContactModalProps> = observer(({ closeModal, hideCreateButton }) => {
  const store = useDependencyInjector(TuvisContactsStore);
  const [searchText, setSearchText] = useState<string>("");
  const { t } = useTranslation();

  const onCancel = useCallback(() => {
    return closeModal();
  }, [closeModal]);

  const filteredContacts = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return store.contacts.filter((contact) => !contact.isDeleted && contact.phones?.length > 0 && contact.displayName.toLowerCase().includes(term));
  }, [searchText, store.contacts.size]);

  return (
    <BaseModal
      backdropClassName={styles.backdrop}
      bodyClassName={styles.modalBody}
      className={styles.modal}
      closeModal={onCancel}
      closeOnClickOutside={false}
      closeOnEscape={false}
      headerClassName={styles.header}
      header={<ModalHeader closeModal={onCancel}>{t("whatsapp_business_api_agent_page.select_contact_modal.title")}</ModalHeader>}
    >
      <div className={styles.topButtons}>
        <SearchInput
          data-testid={"search_contact_input"}
          placeholder={t("whatsapp_business_api_agent_page.select_contact_modal.search_placeholder")}
          value={searchText ?? ""}
          className={styles.searchInput}
          onChange={(value) => {
            setSearchText(value);
          }}
        />
        {!hideCreateButton && (
          <Button
            testId="create_contact_button"
            onClick={() => store.addContact()}
            className={styles.addContactButton}
            svg={{ iconName: `plus`, width: 13, height: 13 }}
            paddingInline={`small`}
            secondary={true}
          />
        )}
      </div>

      <div className={classnames(styles.modalContent)}>
        <>
          {filteredContacts.length === 0 ? (
            <div className={styles.emptyState}>
              <div>
                {store.contacts.size === 0 && !hideCreateButton
                  ? t("whatsapp_business_api_agent_page.select_contact_modal.no_contact")
                  : t("whatsapp_business_api_agent_page.select_contact_modal.no_results")}
              </div>
            </div>
          ) : (
            <div className={styles.contactsContainer}>
              {filteredContacts.map((contact) => {
                return (
                  <div
                    data-testid={"waba_select_contact_list_item"}
                    className={styles.contactRowContainer}
                    key={contact._id}
                    onClick={() => closeModal({ contactId: contact._id })}
                  >
                    <div className={styles.contactRow}>
                      <div className={styles.contactInfo}>
                        <ContactImageComponent
                          className={styles.contactImage}
                          size="small"
                          img={undefined}
                          name={contact.displayName}
                          isGroup={false}
                        />
                        <div className={styles.contactName}>{contact.displayName}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      </div>
    </BaseModal>
  );
});
